/**
 * HOW A PROFILE IS ADDRESSED — one rule, one place (ruling O-21a)
 *
 * `/profil/{id}`. Not `/profil/{username}`, not "either one", and there is no
 * resolution step in between.
 *
 * THE FACT THAT FORCES IT:
 *
 *     SELECT count(*) FROM users a
 *       JOIN users b ON b.username = a.id::text AND b.id <> a.id;   -- 10
 *
 * Ten accounts carry a numeric username equal to a DIFFERENT user's id — and
 * those ten are precisely the real migrated Theta accounts (`10001` is user
 * 179's handle and also user 10001's id; `10181` is user 181's and also user
 * 10181's). The ~100k readable handles are seeded demo data. So a route that
 * accepted both forms would serve the wrong person's profile, for real users,
 * silently, and no amount of care at the call site would prevent it.
 *
 * It is the same ambiguity D8 solved for groups with the
 * `groups_slug_not_numeric` CHECK, except here the numeric form is the one
 * that is REAL, so that constraint is not available and the only safe route is
 * one that accepts nothing but an id.
 *
 * This module exists so that rule is stated once rather than reimplemented at
 * each of the ~19 link sites, and so `parseUserId` — the route's half of it —
 * can be tested without booting a server component.
 */

/**
 * The address of one person's profile.
 *
 * `null` in, `null` out, because "there is nobody to link to" is a real and
 * common answer: an anonymous post, a comment whose author's account is gone
 * (`author_id` is `ON DELETE SET NULL`), the `id: 0` placeholder the event
 * serializer substitutes for a deleted creator. A caller that gets `null`
 * renders the name unlinked; it must never fall back to a handle.
 */
export function profileHref(userId: number | null | undefined): string | null {
  if (userId === null || userId === undefined) return null;
  if (!Number.isSafeInteger(userId) || userId <= 0) return null;
  return `/profil/${userId}`;
}

/**
 * The route's half: a `/profil/[id]` path segment -> a user id, or `null`.
 *
 * `null` means `notFound()`, DELIBERATELY — not "try it as a username". See
 * the note above for why there is no fallback resolution, and none may be
 * added later.
 *
 * The SHAPE is checked before the value, because `Number()` and `parseInt`
 * each accept things a path segment must not be — `"1e3"`, `" 12"`, `"0x0c"`,
 * `"12abc"`, `"+7"` — and every one of those is either a different id or no id
 * at all.
 */
export function parseUserId(raw: string): number | null {
  if (!/^[1-9][0-9]*$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
}
